import { z } from 'zod';
import { environmentSchema, infrastructureStatusSchema } from './infrastructure';
import { timestampSchema } from './common';

/**
 * Backend-for-frontend read models (architecture documentation section 10.1).
 *
 * These shapes exist for the user interface, not for the domain. A KPI tile
 * needs one number and a delta -- so it receives one number and a delta, rather
 * than a page of services the browser would have to count itself.
 */

/**
 * Counting projects is not among these. A deployment is about one project
 * (architecture documentation section 2), so "active projects: 1" is furniture;
 * what a reader wants from that project -- its health, its progress -- is the
 * `project-summary` component's job, and it reads the project record rather
 * than a count over a portfolio that does not exist.
 */
/**
 * Counting failed background jobs is not among these either, and used to be.
 * The queue moved to the provider console with the Operations page: a count a
 * client administrator can read but not act on is a worry rather than a report,
 * and the console shows the same figure beside the jobs that produced it.
 */
export const metricKeySchema = z.enum(['services.total', 'services.degraded', 'services.outage']);
export type MetricKey = z.infer<typeof metricKeySchema>;

export const metricQuerySchema = z.object({
  metric: metricKeySchema,
  environment: environmentSchema.optional(),
});

export const metricResultSchema = z.object({
  metric: metricKeySchema,
  value: z.number(),
  /** Same metric one window earlier; null when no comparable history exists. */
  previousValue: z.number().nullable(),
  /** Direction that counts as good, so the frontend colors the delta correctly. */
  favorableDirection: z.enum(['up', 'down', 'neutral']),
  unit: z.enum(['count', 'percent', 'milliseconds']),
  observedAt: timestampSchema,
});
export type MetricResult = z.infer<typeof metricResultSchema>;

/**
 * Batch endpoint: a dashboard with six KPI tiles should cost one request, not
 * six (section 10.1, "batch endpoints for related component data").
 */
export const metricBatchQuerySchema = z.object({
  metrics: z.array(metricKeySchema).min(1).max(12),
  environment: environmentSchema.optional(),
});

export const metricBatchResultSchema = z.object({
  results: z.array(metricResultSchema),
});

/**
 * One row of the "is anything on fire" rollup.
 *
 * Grouped by type rather than by environment, which is what it used to be.
 * Environment answered the question for one kind of deployment -- an IT estate
 * where the same service exists in production, staging and development -- and
 * answered nothing for a programme responsible for bridges, which exist once.
 * Type is the classification every deployment now chooses for itself
 * (contracts: infrastructure.ts), so the rollup reads "Bridges: one degraded"
 * or "Databases: all operational" depending on what the customer put in it.
 *
 * `typeId` is null for the untyped group, which exists whenever a type has been
 * deleted out from under the things that carried it. It is a real group with a
 * real answer, not an error state.
 */
export const statusGroupSummarySchema = z.object({
  typeId: z.string().uuid().nullable(),
  typeName: z.string().nullable(),
  worstStatus: infrastructureStatusSchema,
  counts: z.object({
    operational: z.number().int().nonnegative(),
    degraded: z.number().int().nonnegative(),
    outage: z.number().int().nonnegative(),
    maintenance: z.number().int().nonnegative(),
    unknown: z.number().int().nonnegative(),
  }),
});
export type StatusGroupSummary = z.infer<typeof statusGroupSummarySchema>;

export const overviewSchema = z.object({
  groups: z.array(statusGroupSummarySchema),
  generatedAt: timestampSchema,
});
export type Overview = z.infer<typeof overviewSchema>;
